import React, { useCallback, useContext, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { AdminAxios as Axios } from '../../Assets/Axios/Axios';

import ToastContext from '../../Context/ToastContext';

import { AdminGetCategories } from '../../Server/Query';
import { CreateCategory, UpdateCategory, DeleteCategory } from '../../Server/Mutation';

import { uploadFileAdmin as uploadFile, UPLOAD_FOLDERS } from '../../Services/S3';

const EMPTY_FORM = { id: null, name: '', description: '', icon: '', isActive: true, showHome: true };

const Categories = () => {
  const { showToastMessage } = useContext(ToastContext);

  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState(null); // null = closed; object = create/edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, errors } = await Axios(AdminGetCategories);
      if (errors || !data?.categories) {
        showToastMessage('Failed to load categories.', 'error');
        return;
      }
      setCategories(data.categories);
    } finally {
      setIsLoading(false);
    }
  }, [showToastMessage]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  async function handleSave() {
    if (!form.name.trim()) {
      showToastMessage('Category name is required.', 'error');
      return;
    }
    setIsSaving(true);
    try {
      // Upload a freshly-picked icon to S3 first; persist the returned URL.
      let iconUrl = form.icon || null;
      if (form.iconFile) {
        iconUrl = await uploadFile(form.iconFile, UPLOAD_FOLDERS.CATEGORY_ICON);
      }

      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        icon: iconUrl,
        isActive: form.isActive,
        showHome: form.showHome,
      };
      const { errors } = await Axios(
        form.id
          ? UpdateCategory({ id: form.id, ...payload })
          : CreateCategory(payload)
      );
      if (errors) {
        showToastMessage(errors[0]?.message ?? 'Failed to save category.', 'error');
        return;
      }
      showToastMessage(`Category ${form.id ? 'updated' : 'created'}.`, 'success');
      setForm(null);
      await fetchCategories();
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleShowHome(category) {
    const nextValue = !category.showHome;
    // optimistic local update
    setCategories(cats =>
      cats.map(c => (c.id === category.id ? { ...c, showHome: nextValue } : c))
    );
    const { errors } = await Axios(
      UpdateCategory({ id: category.id, showHome: nextValue })
    );
    if (errors) {
      showToastMessage(errors[0]?.message ?? 'Failed to update.', 'error');
      // revert on failure
      setCategories(cats =>
        cats.map(c => (c.id === category.id ? { ...c, showHome: !nextValue } : c))
      );
    }
  }

  async function handleDelete() {
    const target = deleteTarget;
    setDeleteTarget(null);
    const { errors } = await Axios(DeleteCategory({ id: target.id }));
    if (errors) {
      // Server returns a clear message when services are linked / protected.
      showToastMessage(errors[0]?.message ?? 'Failed to delete category.', 'error');
      return;
    }
    showToastMessage('Category deleted.', 'success');
    await fetchCategories();
  }

  return (
    <Box p={3}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb={3}>
        <Typography variant='h5' fontWeight={600}>
          Categories
        </Typography>
        <Button variant='contained' onClick={() => setForm({ ...EMPTY_FORM })}>
          Add Category
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell align='center'>Services</TableCell>
              <TableCell align='center'>Active</TableCell>
              <TableCell align='center'>Show on home</TableCell>
              <TableCell align='right'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading && categories.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  No categories yet.
                </TableCell>
              </TableRow>
            )}
            {categories.map(category => {
              const hasServices = category.serviceCount > 0;
              const deleteDisabled = category.isProtected || hasServices;
              const deleteReason = category.isProtected
                ? 'The "Other" category is protected and cannot be deleted'
                : hasServices
                  ? `${category.serviceCount} service(s) linked — reassign them first`
                  : 'Delete category';
              return (
                <TableRow key={category.id}>
                  <TableCell>
                    {category.name}
                    {category.isProtected && (
                      <Chip label='Protected' size='small' sx={{ ml: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{category.slug}</TableCell>
                  <TableCell align='center'>{category.serviceCount}</TableCell>
                  <TableCell align='center'>{category.isActive ? 'Yes' : 'No'}</TableCell>
                  <TableCell align='center'>
                    <Switch
                      size='small'
                      checked={Boolean(category.showHome)}
                      onChange={() => handleToggleShowHome(category)}
                    />
                  </TableCell>
                  <TableCell align='right'>
                    <IconButton
                      onClick={() =>
                        setForm({
                          id: category.id,
                          name: category.name,
                          description: category.description ?? '',
                          icon: category.icon ?? '',
                          isActive: category.isActive,
                          showHome: category.showHome,
                        })
                      }
                    >
                      <EditIcon />
                    </IconButton>
                    <Tooltip title={deleteReason}>
                      <span>
                        <IconButton
                          color='error'
                          disabled={deleteDisabled}
                          onClick={() => setDeleteTarget(category)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit dialog */}
      <Dialog open={Boolean(form)} onClose={() => setForm(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{form?.id ? 'Edit Category' : 'Add Category'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label='Name'
              required
              value={form?.name ?? ''}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              helperText='The URL slug is generated from the name automatically.'
            />
            <TextField
              label='Description'
              multiline
              minRows={2}
              value={form?.description ?? ''}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
            <Box>
              <Typography variant='body2' sx={{ mb: 0.5 }}>
                Icon
              </Typography>
              {(form?.iconFile || form?.icon) && (
                <Box
                  component='img'
                  alt='Category icon'
                  src={form.iconFile ? URL.createObjectURL(form.iconFile) : form.icon}
                  sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, mb: 1, display: 'block' }}
                />
              )}
              <Button variant='outlined' component='label' size='small'>
                {form?.iconFile || form?.icon ? 'Change icon' : 'Upload icon'}
                <input
                  hidden
                  type='file'
                  accept='image/*'
                  onChange={e =>
                    setForm(f => ({ ...f, iconFile: e.target.files?.[0] ?? null }))
                  }
                />
              </Button>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={form?.isActive ?? true}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                />
              }
              label='Active'
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form?.showHome ?? true}
                  onChange={e => setForm(f => ({ ...f, showHome: e.target.checked }))}
                />
              }
              label='Show on home page'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)}>Cancel</Button>
          <Button variant='contained' onClick={handleSave} disabled={isSaving}>
            {form?.id ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color='error' variant='contained' onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;
